import { useEffect } from "react";
import type { Topic } from "@/pages/home/types";
import { formatCount, formatHeat } from "@/utils/format";

interface TopicModalProps {
  topic: Topic | null;
  related: Topic[];
  onClose: () => void;
  onSelect: (topic: Topic) => void;
}

export default function TopicModal({ topic, related, onClose, onSelect }: TopicModalProps) {
  useEffect(() => {
    if (!topic) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [topic, onClose]);

  if (!topic) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${topic.title} 话题详情`}
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-lg bg-background-50 sm:rounded-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative h-44 w-full md:h-56">
          <img
            src={topic.image}
            alt={`${topic.title} 话题配图`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
          <button
            type="button"
            aria-label="关闭话题详情"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md bg-foreground-950/50 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/80"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
              {topic.categoryName}
            </span>
            <h3 className="mt-2 font-heading text-lg font-bold text-background-50 md:text-2xl">
              {topic.title}
            </h3>
          </div>
        </div>

        <div className="p-5 md:p-7">
          <div className="flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-xs text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true" />
              {topic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" aria-hidden="true" />
              {topic.time}
            </span>
            <span className="flex items-center gap-1.5 text-primary-600">
              <i className="ri-fire-line" aria-hidden="true" />
              热度 {formatHeat(topic.heat)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" aria-hidden="true" />
              阅读 {formatCount(topic.views)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-chat-3-line" aria-hidden="true" />
              讨论 {formatCount(topic.comments)}
            </span>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-foreground-700">{topic.summary}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {topic.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800"
              >
                #{tag}
              </span>
            ))}
          </div>

          {related.length > 0 ? (
            <div className="mt-7">
              <h4 className="font-heading text-sm font-bold text-foreground-900">相关话题推荐</h4>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {related.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item)}
                    className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 p-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={item.image}
                        alt={`${item.title} 话题缩略图`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-xs font-medium leading-snug text-foreground-800">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-primary-600">
                        热度 {formatHeat(item.heat)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}